import React, { useState, useRef } from "react";
import { api, fmtMoney } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Upload, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function ImportWizard() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const ref = useRef();

  const choose = (e) => { setFile(e.target.files?.[0] || null); setPreview(null); };

  const runPreview = async () => {
    if (!file) { toast.error("Choose an .xlsx file"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file || !preview?.valid) return;
    setCommitting(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("skip_duplicates", "true");
      const r = await api.post("/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.imported} vehicles, ${r.data.sales_created} sales created, ${r.data.skipped_duplicates} duplicates skipped`);
      setFile(null); setPreview(null); if (ref.current) ref.current.value = "";
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Import failed"); }
    finally { setCommitting(false); }
  };

  return (
    <div data-testid="import-wizard">
      <PageHeader
        overline="Bulk Import"
        title="Import Vehicles"
        description="Upload your spreadsheet with the exact sheet names 'Auction Purchases' and 'Facebook Purchases'."
      />

      <Card className="p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <FileSpreadsheet size={32} className="text-blue-600" />
          <div>
            <h3 className="font-display text-lg font-semibold">Required file format</h3>
            <p className="text-sm text-muted-foreground">Excel (.xlsx) with two named worksheets and exact column headers.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="surface p-4">
            <div className="font-semibold mb-2">Sheet: <code className="text-blue-600">Auction Purchases</code></div>
            <div className="text-xs text-muted-foreground font-mono">Date · Vehicle · VIN · Auction Total Cost · 9.1% Tax · Dealer Fee · Repair · Final Total Cost · Sold for</div>
          </div>
          <div className="surface p-4">
            <div className="font-semibold mb-2">Sheet: <code className="text-blue-600">Facebook Purchases</code></div>
            <div className="text-xs text-muted-foreground font-mono">Date · Vehicle · VIN · Price · Repairs · Final Total Cost · Sold Price · Sold Date</div>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <input ref={ref} data-testid="import-file" type="file" accept=".xlsx" onChange={choose}
            className="block text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700" />
          <Button data-testid="import-preview-btn" onClick={runPreview} disabled={!file || busy}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Upload size={14} className="mr-2" />} Preview
          </Button>
        </div>
      </Card>

      {preview && !preview.valid && (
        <Card className="p-6 border-red-200 bg-red-50/50 dark:bg-red-500/10">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-semibold mb-3">
            <AlertCircle size={18} /> Validation errors
          </div>
          <ul className="text-sm space-y-1 text-red-700 dark:text-red-300 list-disc pl-5">
            {preview.errors.map((er) => <li key={er}>{er}</li>)}
          </ul>
        </Card>
      )}

      {preview?.valid && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Card className="p-5"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total Rows</div><div className="font-display text-3xl font-bold mt-1">{preview.total_rows}</div></Card>
            <Card className="p-5"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">To Import</div><div className="font-display text-3xl font-bold mt-1 text-emerald-600">{preview.rows_to_import}</div></Card>
            <Card className="p-5"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Rows with Errors</div><div className="font-display text-3xl font-bold mt-1 text-amber-600">{preview.rows_with_errors}</div></Card>
            <Card className="p-5"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Duplicate VINs</div><div className="font-display text-3xl font-bold mt-1 text-red-600">{preview.duplicate_vins.length}</div></Card>
          </div>

          {preview.duplicate_vins.length > 0 && (
            <Card className="p-4 mb-4 bg-amber-50/60 dark:bg-amber-500/10">
              <div className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-2">Duplicate VIN warnings (will be skipped):</div>
              <div className="text-xs text-amber-800 dark:text-amber-200 font-mono space-y-0.5">
                {preview.duplicate_vins.slice(0, 10).map((d) => <div key={d.vin}>{d.vin} — {d.vehicle}</div>)}
              </div>
            </Card>
          )}

          <Card className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                <tr>{["Source", "Vehicle", "VIN", "Purchase", "Tax", "Dealer Fee", "Repair", "Sale", "Status"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {preview.preview.map((row, i) => (
                  <tr key={`${row.vin || "novin"}-${row.kind}-${row.vehicle_name}-${i}`} className={`border-t border-border ${row.duplicate ? "bg-amber-50/40 dark:bg-amber-500/5" : ""}`}>
                    <td className="px-4 py-3 capitalize">{row.kind}</td>
                    <td className="px-4 py-3 font-medium">{row.vehicle_name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{row.vin || "—"}</td>
                    <td className="px-4 py-3">{fmtMoney(row.purchase_price)}</td>
                    <td className="px-4 py-3">{fmtMoney(row.tax_cost)}</td>
                    <td className="px-4 py-3">{fmtMoney(row.dealer_fee)}</td>
                    <td className="px-4 py-3">{fmtMoney(row.repair_cost)}</td>
                    <td className="px-4 py-3">{fmtMoney(row.sale_price)}</td>
                    <td className="px-4 py-3 capitalize">{row.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <div className="flex justify-end">
            <Button data-testid="import-commit-btn" onClick={commit} disabled={committing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg">
              {committing ? <Loader2 size={14} className="animate-spin mr-2" /> : <CheckCircle2 size={14} className="mr-2" />}
              Import {preview.rows_to_import} Vehicles
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
